/**
 * React context and hook for SensitiveDataSessionStore.
 * Wraps the pure reducer in sensitive-data-session.ts.
 *
 * Import path for consumers: "@/lib/sensitive-data-session-provider"
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";

import {
  sessionReducer,
  INITIAL_SESSION_STATE,
  type SensitiveDataSession,
} from "./sensitive-data-session";

const SensitiveDataSessionContext =
  createContext<SensitiveDataSession | null>(null);

export function SensitiveDataSessionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(sessionReducer, INITIAL_SESSION_STATE);

  const setValue = useCallback((fieldId: string, value: string) => {
    dispatch({ type: "SET", fieldId, value });
  }, []);

  const clearValue = useCallback((fieldId: string) => {
    dispatch({ type: "CLEAR", fieldId });
  }, []);

  const markSaved = useCallback((fieldIds: string[]) => {
    dispatch({ type: "MARK_SAVED", fieldIds });
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: "CLEAR_ALL" });
  }, []);

  const completeSetup = useCallback(
    (handle: FileSystemFileHandle | null, passphrase: string) => {
      dispatch({ type: "COMPLETE_SETUP", fileHandle: handle, passphrase });
    },
    []
  );

  const resetSetup = useCallback(() => {
    dispatch({ type: "RESET_SETUP" });
  }, []);

  const loadValues = useCallback((values: Record<string, string>) => {
    dispatch({ type: "LOAD", values });
  }, []);

  // Cleanup on provider unmount.
  //
  // (1) WHY CLEAR_ALL: this is defensive — React already releases fiber memory (including
  //     useReducer state) when a component unmounts, so the passphrase and field values
  //     are gone regardless. The dispatch is belt-and-suspenders signalling intent.
  //
  // (2) ERROR BOUNDARY SAFETY: if an error boundary catches a child render error, the
  //     provider itself is not unmounted (assuming the boundary sits inside the provider,
  //     the normal placement). Cleanup therefore does NOT fire in that case — which is
  //     correct: the provider state is still live and valid. If the boundary wraps the
  //     entire provider, the provider unmounts and React GCs the fiber; again safe.
  //
  // (3) TAB-CLOSE DESIGN (Q4c): when the user closes the tab on Safari/Firefox without
  //     downloading, this cleanup may fire but is a no-op (React is already tearing down).
  //     The in-memory values are lost. This is intentional per the session 27 design
  //     conversation (Q4c): tab close = values lost, no record kept. The Q4a amber banner
  //     is the prevention; lossy tab-close is the architecturally clean correction.
  //
  // Phase 3 checklist: wrap `session` in useMemo (deps: state, stable callbacks) before
  // SensitiveFieldInput dispatcher wiring — context value is currently a new object literal
  // every render, which will cause all 50+ field consumers to re-render on each keystroke.
  useEffect(() => {
    return () => {
      dispatch({ type: "CLEAR_ALL" });
    };
  }, []);

  const dirtyFieldIds = useMemo(
    () =>
      Object.entries(state.fields)
        .filter(([, rec]) => !rec.saved)
        .map(([id]) => id),
    [state.fields]
  );

  const publicValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(state.fields).map(([id, rec]) => [id, rec.value])
      ),
    [state.fields]
  );

  // useMemo so that the context value object is stable between renders when
  // nothing has changed — prevents all 50+ SensitiveFieldInput consumers from
  // re-rendering on every keystroke in any one field. Deps: derived arrays +
  // stable callbacks (guaranteed by useCallback above).
  const session: SensitiveDataSession = useMemo(
    () => ({
      values: publicValues,
      setValue,
      getValue: (key: string) => publicValues[key],
      clearValue,
      markSaved,
      getAllValues: () => publicValues,
      loadValues,
      hasValues: Object.keys(publicValues).length > 0,
      unsavedCount: dirtyFieldIds.length,
      getDirtyFields: () => dirtyFieldIds,
      hasUnsavedChanges: dirtyFieldIds.length > 0,
      clearAll,
      isSetupComplete: state.setupComplete,
      fileHandle: state.fileHandle as FileSystemFileHandle | null,
      passphrase: state.passphrase,
      completeSetup,
      resetSetup,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      publicValues,
      dirtyFieldIds,
      state.setupComplete,
      state.fileHandle,
      state.passphrase,
      setValue,
      clearValue,
      markSaved,
      loadValues,
      clearAll,
      completeSetup,
      resetSetup,
    ]
  );

  return (
    <SensitiveDataSessionContext.Provider value={session}>
      {children}
    </SensitiveDataSessionContext.Provider>
  );
}

export function useSensitiveDataSession(): SensitiveDataSession {
  const ctx = useContext(SensitiveDataSessionContext);
  if (!ctx) {
    throw new Error(
      "useSensitiveDataSession must be used within SensitiveDataSessionProvider"
    );
  }
  return ctx;
}

/**
 * s227 (LOGOUT-SAVE-CHECK-1) — the session when a provider is mounted, else null. For code that runs on every
 * sign-out path, including roles that never hold Cat A values; everything else keeps the throwing hook above.
 */
export function useOptionalSensitiveDataSession(): SensitiveDataSession | null {
  return useContext(SensitiveDataSessionContext);
}
